<template>
  <div class="rush-container" ref="container">
    <SharedProgressBar
      v-if="phase === 'playing'"
      :total-score="totalScore"
      :target="target"
      :players="players"
      :combo="combo"
      :won="gameWon"
    />

    <!-- Timer -->
    <div v-if="phase === 'playing'" class="timer-bar">
      <div class="timer-fill" :style="{ width: `${timerPercent}%` }" :class="{ 'timer-danger': timeLeft <= 10 }"></div>
      <span class="timer-text">{{ timeLeft }}s</span>
    </div>

    <!-- Score Flash -->
    <Transition name="flash">
      <div v-if="flashText" :key="flashKey" class="flash-text" :class="flashClass">{{ flashText }}</div>
    </Transition>

    <!-- Game Canvas -->
    <canvas v-show="phase === 'playing'" ref="canvas" class="game-canvas" @touchstart="onTouchStart" @touchmove="onTouchMove" @touchend="onTouchEnd" @mousedown="onMouseDown"></canvas>

    <!-- Start Overlay -->
    <div v-if="phase === 'idle'" class="overlay">
      <div class="text-5xl mb-4">⚡</div>
      <h2 class="text-2xl font-bold text-white mb-2">Rush Hour</h2>
      <p class="text-white/60 text-sm mb-1">Swipe Zutaten in den richtigen Bon!</p>
      <p class="text-white/40 text-xs mb-6">Alleine oder als Team – ihr entscheidet!</p>
      <div class="flex gap-3">
        <button @click="startSolo" class="start-btn">🎯 Solo</button>
        <button @click="startMultiplayer" class="start-btn multiplayer-btn">👥 Tisch-Modus</button>
      </div>
    </div>

    <!-- Lobby Overlay -->
    <div v-if="phase === 'lobby'" class="overlay">
      <div class="text-4xl mb-3">⚡</div>
      <h2 class="text-xl font-bold text-white mb-1">Rush Hour – Lobby</h2>
      <p class="text-white/50 text-xs mb-4">Warte, bis alle am Tisch beigetreten sind</p>

      <div class="lobby-players">
        <div v-for="p in players" :key="p.id" class="lobby-player">
          <div class="lobby-avatar" :class="{ 'host-ring': p.isHost }">
            {{ (p.name || '?')[0].toUpperCase() }}
          </div>
          <span class="text-white text-sm">{{ p.name }}</span>
          <span v-if="p.isHost" class="text-yellow-400 text-[10px] ml-1">HOST</span>
        </div>
      </div>

      <p class="text-white/40 text-xs mt-3 mb-4">
        {{ players.length }} Spieler · Ziel: {{ target }} Punkte in {{ duration }}s
      </p>

      <button
        v-if="isHost"
        @click="launchGame"
        class="start-btn w-full"
        :disabled="players.length < 1"
      >Spiel starten ({{ players.length }} Spieler)</button>

      <p v-else class="text-white/50 text-sm animate-pulse">Warte auf Host...</p>
    </div>

    <!-- Result Overlay -->
    <div v-if="phase === 'result'" class="overlay">
      <div class="text-5xl mb-4">{{ gameWon ? '🏆' : '⏰' }}</div>
      <h2 class="text-2xl font-bold text-white mb-2">{{ gameWon ? 'Geschafft!' : 'Zeit abgelaufen!' }}</h2>
      <p class="text-white/60 mb-4">{{ myScore }} korrekte Swipes</p>
      <div v-if="reward" class="reward-box mb-4">
        <p class="text-yellow-400 font-bold">{{ reward.message }}</p>
      </div>
      <button @click="$emit('close')" class="start-btn">Schließen</button>
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted, onUnmounted, nextTick, computed } from 'vue'
import SharedProgressBar from './SharedProgressBar.vue'
import { gamificationService } from '@/services/api/gamificationService'

const props = defineProps({
  existingGameId: { type: String, default: null },
})
const emit = defineEmits(['close', 'apply-discount'])

const container = ref(null)
const canvas = ref(null)
let ctx = null
let animFrame = null

const phase = ref('idle')
const myScore = ref(0)
const totalScore = ref(0)
const target = ref(50)
const duration = ref(60)
const timeLeft = ref(60)
const combo = ref(0)
const gameWon = ref(false)
const players = ref([])
const reward = ref(null)
const flashText = ref(null)
const flashKey = ref(0)
const flashClass = ref('')
const isHost = ref(false)

let gameSessionId = null
let timerInterval = null
let lobbyPollInterval = null
const myId = localStorage.getItem('sessionUniqueId') || localStorage.getItem('sessionId') || ''

const bons = ref([])
const currentIngredient = ref(null)
let isDragging = false
let ingredientX = 0
let ingredientY = 0

const BON_TYPES = [
  { id: 'burger', label: 'Burger', color: '#E74C3C', emoji: '🍔', ingredients: ['patty', 'lettuce', 'cheese', 'onion', 'bun'] },
  { id: 'cocktail', label: 'Cocktail', color: '#3498DB', emoji: '🍹', ingredients: ['lime', 'ice', 'rum', 'mint', 'sugar'] },
  { id: 'pizza', label: 'Pizza', color: '#F39C12', emoji: '🍕', ingredients: ['dough', 'tomato', 'mozzarella', 'basil', 'olive'] },
  { id: 'salad', label: 'Salat', color: '#27AE60', emoji: '🥗', ingredients: ['lettuce_s', 'cucumber', 'tomato_s', 'avocado', 'dressing'] },
]

const INGREDIENTS = {
  patty: { emoji: '🥩', label: 'Patty' }, lettuce: { emoji: '🥬', label: 'Salat' },
  cheese: { emoji: '🧀', label: 'Käse' }, onion: { emoji: '🧅', label: 'Zwiebel' },
  bun: { emoji: '🍞', label: 'Brötchen' }, lime: { emoji: '🍋', label: 'Limette' },
  ice: { emoji: '🧊', label: 'Eis' }, rum: { emoji: '🥃', label: 'Rum' },
  mint: { emoji: '🌿', label: 'Minze' }, sugar: { emoji: '🍬', label: 'Zucker' },
  dough: { emoji: '🫓', label: 'Teig' }, tomato: { emoji: '🍅', label: 'Tomate' },
  mozzarella: { emoji: '🧀', label: 'Mozz.' }, basil: { emoji: '🌱', label: 'Basilikum' },
  olive: { emoji: '🫒', label: 'Olive' }, lettuce_s: { emoji: '🥬', label: 'Salat' },
  cucumber: { emoji: '🥒', label: 'Gurke' }, tomato_s: { emoji: '🍅', label: 'Tomate' },
  avocado: { emoji: '🥑', label: 'Avocado' }, dressing: { emoji: '🥫', label: 'Dressing' },
}

const timerPercent = computed(() => (timeLeft.value / Math.max(1, duration.value)) * 100)

// --- Lobby & Session Management ---

async function startSolo() {
  await createOrJoinSession()
  beginPlaying()
}

async function startMultiplayer() {
  await createOrJoinSession()
  phase.value = 'lobby'
  isHost.value = !props.existingGameId
  startLobbyPolling()
}

async function createOrJoinSession() {
  const tableId = parseInt(localStorage.getItem('tableId')) || 0
  try {
    let data
    if (props.existingGameId) {
      const res = await gamificationService.joinMiniGame(props.existingGameId, {
        sessionIdentifier: myId, tableId, playerName: 'Spieler',
      })
      data = res.data
      isHost.value = false
    } else {
      const res = await gamificationService.createMiniGameSession({
        gameType: 'rush_hour', tableId, sessionIdentifier: myId,
        orderSessionId: parseInt(localStorage.getItem('sessionNumericId')) || null,
      })
      data = res.data
      isHost.value = true
    }
    gameSessionId = data.id
    target.value = data.target
    duration.value = data.duration || 60
    timeLeft.value = data.duration || 60
    players.value = data.players || []
  } catch (e) {
    gameSessionId = null
    target.value = 50
    duration.value = 60
    timeLeft.value = 60
  }
}

function startLobbyPolling() {
  stopLobbyPolling()
  lobbyPollInterval = setInterval(async () => {
    if (!gameSessionId || phase.value !== 'lobby') return
    try {
      const { data } = await gamificationService.getMiniGameSession(gameSessionId)
      if (!data) return
      players.value = data.players || []
      target.value = data.target
      duration.value = data.duration || duration.value
      timeLeft.value = data.duration || timeLeft.value
      if (data.status === 'playing') {
        stopLobbyPolling()
        beginPlaying()
      }
    } catch (e) { /* retry next tick */ }
  }, 2000)
}

function stopLobbyPolling() {
  if (lobbyPollInterval) { clearInterval(lobbyPollInterval); lobbyPollInterval = null }
}

async function launchGame() {
  if (!gameSessionId) return
  try {
    await gamificationService.startMiniGame(gameSessionId)
  } catch (e) { /* */ }
  stopLobbyPolling()
  beginPlaying()
}

// Auto-join if existingGameId was provided
onMounted(() => {
  if (props.existingGameId) {
    startMultiplayer()
  }
})

// --- Game Logic ---

function beginPlaying() {
  phase.value = 'playing'
  myScore.value = 0
  totalScore.value = 0
  combo.value = 0
  gameWon.value = false
  reward.value = null

  nextTick(() => {
    setupCanvas()
    initBons()
    spawnIngredient()
    draw()
    timerInterval = setInterval(() => {
      timeLeft.value--
      if (timeLeft.value <= 0) endGame(totalScore.value >= target.value)
    }, 1000)
  })
}

function spawnIngredient() {
  const bonType = BON_TYPES[Math.floor(Math.random() * bons.value.length)]
  const ingKey = bonType.ingredients[Math.floor(Math.random() * bonType.ingredients.length)]
  const w = canvas.value?.width || 300
  const h = canvas.value?.height || 500
  currentIngredient.value = {
    key: ingKey,
    correctBon: bonType.id,
    ...INGREDIENTS[ingKey],
    x: w / 2, y: h * 0.4,
    baseX: w / 2, baseY: h * 0.4,
  }
  ingredientX = currentIngredient.value.x
  ingredientY = currentIngredient.value.y
}

function initBons() {
  const w = canvas.value?.width || 300
  const h = canvas.value?.height || 500
  const count = Math.min(4, BON_TYPES.length)
  const spacing = w / count
  bons.value = BON_TYPES.slice(0, count).map((bt, i) => ({
    ...bt,
    x: spacing * i + spacing / 2,
    y: h - 70,
    w: spacing - 16,
    h: 60,
    flash: 0,
  }))
}

function draw() {
  if (!ctx || !canvas.value) return
  const w = canvas.value.width
  const h = canvas.value.height

  ctx.clearRect(0, 0, w, h)

  ctx.strokeStyle = 'rgba(255,255,255,0.06)'
  ctx.lineWidth = 2
  ctx.setLineDash([8, 8])
  ctx.beginPath()
  ctx.moveTo(0, h - 100)
  ctx.lineTo(w, h - 100)
  ctx.stroke()
  ctx.setLineDash([])

  for (const bon of bons.value) {
    const alpha = bon.flash > 0 ? 1 : 0.8
    ctx.save()
    ctx.globalAlpha = alpha

    ctx.fillStyle = bon.flash > 0 ? (bon.flash === 2 ? 'rgba(231,76,60,0.3)' : 'rgba(46,204,113,0.3)') : 'rgba(255,255,255,0.04)'
    ctx.beginPath()
    roundRect(ctx, bon.x - bon.w / 2, bon.y - bon.h / 2, bon.w, bon.h, 12)
    ctx.fill()

    ctx.strokeStyle = bon.flash > 0 ? (bon.flash === 2 ? '#E74C3C' : '#2ECC71') : 'rgba(255,255,255,0.1)'
    ctx.lineWidth = 1.5
    ctx.beginPath()
    roundRect(ctx, bon.x - bon.w / 2, bon.y - bon.h / 2, bon.w, bon.h, 12)
    ctx.stroke()

    ctx.font = '24px sans-serif'
    ctx.textAlign = 'center'
    ctx.fillText(bon.emoji, bon.x, bon.y + 2)

    ctx.font = 'bold 9px sans-serif'
    ctx.fillStyle = 'rgba(255,255,255,0.5)'
    ctx.fillText(bon.label, bon.x, bon.y + bon.h / 2 - 4)

    ctx.restore()
    if (bon.flash > 0) bon.flash = Math.max(0, bon.flash - 0.02)
  }

  if (currentIngredient.value && phase.value === 'playing') {
    const ing = currentIngredient.value
    const x = isDragging ? ingredientX : ing.baseX
    const y = isDragging ? ingredientY : ing.baseY

    ctx.save()
    ctx.shadowColor = 'rgba(124,58,237,0.4)'
    ctx.shadowBlur = 20
    ctx.fillStyle = 'rgba(255,255,255,0.06)'
    ctx.beginPath()
    ctx.arc(x, y, 36, 0, Math.PI * 2)
    ctx.fill()

    ctx.font = '32px sans-serif'
    ctx.textAlign = 'center'
    ctx.textBaseline = 'middle'
    ctx.fillText(ing.emoji, x, y - 4)

    ctx.font = 'bold 10px sans-serif'
    ctx.fillStyle = 'rgba(255,255,255,0.7)'
    ctx.fillText(ing.label, x, y + 24)

    ctx.restore()
  }

  if (phase.value === 'playing') animFrame = requestAnimationFrame(draw)
}

function roundRect(c, x, y, w, h, r) {
  c.moveTo(x + r, y)
  c.lineTo(x + w - r, y)
  c.quadraticCurveTo(x + w, y, x + w, y + r)
  c.lineTo(x + w, y + h - r)
  c.quadraticCurveTo(x + w, y + h, x + w - r, y + h)
  c.lineTo(x + r, y + h)
  c.quadraticCurveTo(x, y + h, x, y + h - r)
  c.lineTo(x, y + r)
  c.quadraticCurveTo(x, y, x + r, y)
}

function checkDrop() {
  if (!currentIngredient.value) return
  const ing = currentIngredient.value

  for (const bon of bons.value) {
    const dx = Math.abs(ingredientX - bon.x)
    const dy = Math.abs(ingredientY - bon.y)
    if (dx < bon.w / 2 + 20 && dy < bon.h / 2 + 30) {
      if (bon.id === ing.correctBon) {
        myScore.value++
        combo.value++
        bon.flash = 1
        showFlash('+1', 'flash-good')
        if (navigator.vibrate) navigator.vibrate(30)
      } else {
        myScore.value = Math.max(0, myScore.value - 1)
        combo.value = 0
        bon.flash = 2
        showFlash('-1', 'flash-bad')
        shakeScreen()
        if (navigator.vibrate) navigator.vibrate([20, 10, 20])
      }
      syncScore()
      spawnIngredient()
      return
    }
  }
  resetIngredientPos()
}

function showFlash(text, cls) {
  flashText.value = text
  flashClass.value = cls
  flashKey.value++
  setTimeout(() => { flashText.value = null }, 600)
}

function shakeScreen() {
  if (!container.value) return
  container.value.classList.add('screen-shake')
  setTimeout(() => container.value?.classList.remove('screen-shake'), 300)
}

function resetIngredientPos() {
  if (!currentIngredient.value) return
  ingredientX = currentIngredient.value.baseX
  ingredientY = currentIngredient.value.baseY
}

function onTouchStart(e) {
  if (phase.value !== 'playing' || !currentIngredient.value) return
  e.preventDefault()
  const t = e.touches[0]
  const rect = canvas.value.getBoundingClientRect()
  const scaleX = canvas.value.width / rect.width
  const scaleY = canvas.value.height / rect.height
  ingredientX = (t.clientX - rect.left) * scaleX
  ingredientY = (t.clientY - rect.top) * scaleY
  isDragging = true
}
function onTouchMove(e) {
  if (!isDragging) return
  e.preventDefault()
  const t = e.touches[0]
  const rect = canvas.value.getBoundingClientRect()
  const scaleX = canvas.value.width / rect.width
  const scaleY = canvas.value.height / rect.height
  ingredientX = (t.clientX - rect.left) * scaleX
  ingredientY = (t.clientY - rect.top) * scaleY
}
function onTouchEnd() {
  if (!isDragging) return
  isDragging = false
  checkDrop()
}

function onMouseDown() {
  if (phase.value !== 'playing' || !currentIngredient.value) return
  isDragging = true
  const rect = canvas.value.getBoundingClientRect()
  const scaleX = canvas.value.width / rect.width
  const scaleY = canvas.value.height / rect.height
  const move = (ev) => {
    ingredientX = (ev.clientX - rect.left) * scaleX
    ingredientY = (ev.clientY - rect.top) * scaleY
  }
  const up = () => {
    isDragging = false
    document.removeEventListener('mousemove', move)
    document.removeEventListener('mouseup', up)
    checkDrop()
  }
  document.addEventListener('mousemove', move)
  document.addEventListener('mouseup', up)
}

async function syncScore() {
  totalScore.value = myScore.value
  if (gameSessionId) {
    try {
      const { data } = await gamificationService.submitMiniGameScore(gameSessionId, {
        sessionIdentifier: myId,
        score: myScore.value,
        comboBreak: combo.value === 0,
      })
      totalScore.value = data.totalScore
      players.value = data.players || []
      if (data.won) endGame(true)
    } catch (e) { /* offline fallback */ }
  }
}

function setupCanvas() {
  if (!canvas.value || !container.value) return
  const rect = container.value.getBoundingClientRect()
  canvas.value.width = rect.width * window.devicePixelRatio
  canvas.value.height = (rect.height - 80) * window.devicePixelRatio
  canvas.value.style.width = rect.width + 'px'
  canvas.value.style.height = (rect.height - 80) + 'px'
  ctx = canvas.value.getContext('2d')
  ctx.scale(window.devicePixelRatio, window.devicePixelRatio)
  canvas.value.width = rect.width
  canvas.value.height = rect.height - 80
}

async function endGame(won) {
  clearInterval(timerInterval)
  cancelAnimationFrame(animFrame)
  phase.value = 'result'
  gameWon.value = won

  if (gameSessionId) {
    try {
      const { data } = await gamificationService.finishMiniGame(gameSessionId)
      reward.value = data.reward
      if (data.reward) emit('apply-discount', { ...data.reward, gameName: 'Rush Hour' })
    } catch (e) { /* */ }
  }
}

onUnmounted(() => {
  clearInterval(timerInterval)
  cancelAnimationFrame(animFrame)
  stopLobbyPolling()
})
</script>

<style scoped>
.rush-container {
  @apply relative w-full h-full min-h-[480px] bg-[#0a0a14] overflow-hidden rounded-2xl;
}
.game-canvas {
  @apply w-full block;
  touch-action: none;
}
.timer-bar {
  @apply relative h-1.5 mx-4 mt-1 rounded-full overflow-hidden;
  background: rgba(255,255,255,0.06);
}
.timer-fill {
  @apply absolute inset-y-0 left-0 rounded-full transition-all duration-300;
  background: linear-gradient(90deg, #7C3AED, #06B6D4);
}
.timer-danger { background: linear-gradient(90deg, #E74C3C, #FF6B6B) !important; }
.timer-text {
  @apply absolute right-1 top-1/2 -translate-y-1/2 text-[10px] text-white/50 font-mono;
}
.overlay {
  @apply absolute inset-0 flex flex-col items-center justify-center z-20;
  background: rgba(10,10,20,0.92);
  backdrop-filter: blur(8px);
}
.start-btn {
  @apply px-8 py-3 bg-gradient-to-r from-purple-600 to-indigo-600 text-white font-bold rounded-xl;
  @apply hover:from-purple-500 hover:to-indigo-500 transition-all duration-200 active:scale-95;
}
.multiplayer-btn {
  @apply from-cyan-500 to-blue-600 hover:from-cyan-400 hover:to-blue-500;
}
.reward-box {
  @apply p-4 bg-yellow-500/10 border border-yellow-500/30 rounded-xl text-center;
}
.flash-text {
  @apply absolute top-1/3 left-1/2 -translate-x-1/2 text-3xl font-black z-30 pointer-events-none;
  animation: flashUp 0.6s ease forwards;
}
.flash-good { color: #2ECC71; }
.flash-bad { color: #E74C3C; }
.screen-shake { animation: shake 0.3s ease; }

.lobby-players {
  @apply flex flex-wrap justify-center gap-3 mt-2 mb-2;
}
.lobby-player {
  @apply flex items-center gap-2 bg-white/5 border border-white/10 rounded-xl px-3 py-2;
}
.lobby-avatar {
  @apply w-8 h-8 rounded-full bg-purple-600 flex items-center justify-center text-white text-sm font-bold;
}
.host-ring {
  @apply ring-2 ring-yellow-400;
}

@keyframes flashUp {
  0% { opacity: 1; transform: translate(-50%, 0) scale(1); }
  100% { opacity: 0; transform: translate(-50%, -40px) scale(1.5); }
}
@keyframes shake {
  0%, 100% { transform: translateX(0); }
  20% { transform: translateX(-4px); }
  40% { transform: translateX(4px); }
  60% { transform: translateX(-3px); }
  80% { transform: translateX(3px); }
}

.flash-enter-active { animation: flashUp 0.6s ease; }
.flash-leave-active { display: none; }
</style>
